// Compensated hours — claiming work the clock never saw, crediting it to
// someone else in one step, or editing a credit already given.
//
// Deciding claims is not here: the manager's queue (My Space) and HR's
// (People → Requests & approvals) are CompHoursDecisions in requests.tsx, on
// the shared decision row with a reason the claimant sees.
import { useState } from 'react';
import { Button, Select } from './primitives';
import { Dialog } from './dialog';
import { DateField, Field, TextArea, TextInput } from './field';
import { InlineStatus, useToast } from './feedback';
import { fmtDayKey } from '../lib/dates';
import {
  fileClaim,
  grantHours,
  correctCredit,
  fmtHours,
  type Category,
  type CompHours,
} from '../lib/compHours';

const dayLabel = (iso: string) => fmtDayKey(iso, { weekday: true });

/** Which control a validation message belongs to; 'form' is the server's answer. */
type Problem = { at: 'hours' | 'category' | 'reason' | 'form'; message: string };

/**
 * Claim time that never touched the clock.
 *
 * On the shared Dialog and Field: it ignored Escape, focused nothing, labelled
 * its fields in the non-text ink and drew its own scrim (ME-08, TS-V1). A
 * refusal is tied to its field and announced (TS-V2).
 */
export function CompHoursModal({
  uid,
  uids,
  categories,
  today,
  onClose,
  onFiled,
  mode = 'claim',
  personName,
  correcting,
}: {
  uid: string;
  /**
   * Grant mode only: credit ALL of these people in one insert, rather than
   * just `uid`. Falls back to `[uid]` when omitted, so a single-person grant
   * (HR's per-person Hours tab, the amendment workspace) needs no change.
   * Payroll's bulk credit is the one caller that passes more than one.
   */
  uids?: string[];
  categories: readonly Category[];
  /** Org-local today — the database refuses anything later. */
  today: string;
  onClose: () => void;
  onFiled: () => void;
  /**
   * 'claim' files your own pending request. 'grant' credits SOMEONE ELSE in one
   * step, and is only offered to their reporting manager or people:full — the
   * trigger refuses it otherwise, and refuses it for yourself either way.
   */
  mode?: 'claim' | 'grant';
  /** Whose hours these are, when granting — one name, or several joined with
   *  ", " for a bulk credit. Labels only. */
  personName?: string | null;
  /**
   * An approved credit to edit (bug 71ad284c). The form starts from it, asks
   * for a reason, and submits through correctCredit, which revokes it and
   * credits the corrected row in one atomic call. `uid`, `uids` and `mode` play
   * no part in that write.
   */
  correcting?: Pick<CompHours, 'id' | 'profile_id' | 'date' | 'hours' | 'category' | 'note'>;
}) {
  const toast = useToast();
  const start = {
    date: correcting?.date ?? today,
    hours: correcting ? String(correcting.hours) : '1',
    category: correcting?.category ?? categories[0]?.id ?? '',
    note: correcting?.note ?? '',
  };
  const [date, setDate] = useState(start.date);
  const [hours, setHours] = useState(start.hours);
  const [category, setCategory] = useState(start.category);
  const [note, setNote] = useState(start.note);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const errorAt = (at: Problem['at']) => (problem?.at === at ? problem.message : undefined);
  const grant = mode === 'grant';
  // Revoking a credit to give the same one back is two notices and an audit
  // row for no change.
  const unchanged =
    !!correcting &&
    date === correcting.date &&
    Number(hours) === correcting.hours &&
    category === correcting.category &&
    note.trim() === start.note.trim();

  const submit = async () => {
    if (busy) return;
    const h = Number(hours);
    // Mirrors the CHECK on the column. Better here than as a refused round-trip.
    if (!Number.isFinite(h) || h <= 0 || h > 24 || h * 2 !== Math.floor(h * 2)) {
      setProblem({ at: 'hours', message: 'Hours must be a positive half-hour step, up to 24.' });
      return;
    }
    if (!category) {
      setProblem({ at: 'category', message: 'Pick what the time was for.' });
      return;
    }
    if (unchanged) {
      setProblem({ at: 'form', message: 'Nothing is different yet — change what was wrong.' });
      return;
    }
    if (correcting && !reason.trim()) {
      setProblem({ at: 'reason', message: 'Say why this credit is being changed.' });
      return;
    }
    setBusy(true);
    setProblem(null);
    const { error } = correcting
      ? await correctCredit(correcting.id, { date, hours: h, category, note }, reason)
      : grant
        ? await grantHours({ profileIds: uids ?? [uid], date, hours: h, category, note })
        : await fileClaim({ profileId: uid, date, hours: h, category, note });
    setBusy(false);
    if (error) {
      // The batch is one insert, the edit one call: both all-or-nothing,
      // so a refusal means nothing moved, not just what the DB message names.
      setProblem({
        at: 'form',
        message: correcting
          ? `Nothing was changed — ${error.message}`
          : grant
            ? `Nobody was credited — ${error.message}`
            : error.message,
      });
      return;
    }
    onFiled();
    onClose();
    toast.push({
      tone: 'success',
      message: correcting
        ? `Credit changed to ${fmtHours(h)} for ${dayLabel(date)}.`
        : grant
          ? `Credited ${fmtHours(h)}${personName ? ` to ${personName}` : ''} for ${dayLabel(date)}.`
          : `Claim sent for ${dayLabel(date)} — ${fmtHours(h)} count once it is approved.`,
    });
  };

  return (
    <Dialog
      open
      size="sm"
      title={
        correcting
          ? `Edit compensated hours${personName ? ` — ${personName}` : ''}`
          : grant
            ? `Credit compensated hours${personName ? ` — ${personName}` : ''}`
            : 'Claim compensated hours'
      }
      onClose={onClose}
      dirty={
        note.trim() !== start.note.trim() ||
        hours !== start.hours ||
        date !== start.date ||
        category !== start.category ||
        reason.trim() !== ''
      }
      // Cancel asks "Discard changes?" too, as Escape and the × do.
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {correcting ? 'Save changes' : grant ? 'Credit hours' : 'Submit claim'}
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        {/* Echoes the day in the org's format: the box reads in the browser's
            order (UI-CONVENTIONS §2, Entering a date). */}
        <DateField label="Day" value={date} max={today} onChange={setDate} />
        <Field label="Hours" error={errorAt('hours')}>
          <TextInput
            type="number"
            inputMode="decimal"
            mono
            min={0.5}
            max={24}
            step={0.5}
            value={hours}
            onChange={setHours}
          />
        </Field>
        <Field label="What was it?" error={errorAt('category')}>
          {/* A DOM dropdown, not a native <select>: the OS draws that popup, so
              it keeps the platform's white rows and system highlight inside a
              dark modal no matter what the stylesheet says. Reported as
              "picklist is messy" — legible was not the same as ours. */}
          {(control) => (
            <Select
              {...control}
              value={category}
              options={categories.map((c) => ({ value: c.id, label: c.label }))}
              onChange={setCategory}
              placeholder="Pick a category…"
            />
          )}
        </Field>
        <Field
          label="Note"
          // A grant is inserted already approved (grantHours) — a claim sits
          // pending until its decider approves it. Telling a granter it waits on
          // approval was simply untrue.
          hint={
            correcting
              ? undefined
              : grant
                ? 'These hours are credited straight away.'
                : 'These hours count once your reporting manager approves them.'
          }
        >
          <TextArea
            value={note}
            onChange={setNote}
            placeholder={
              grant || correcting ? 'What the hours are for' : 'Where you were, and who with'
            }
          />
        </Field>
        {correcting && (
          // Stakes rung 2 (UI-CONVENTIONS §5): what changes, stated before the
          // confirm, beside the reason that goes on their record.
          <Field
            label="Reason for the change"
            required
            error={errorAt('reason')}
            hint={`Revokes ${fmtHours(correcting.hours)} for ${dayLabel(correcting.date)} and credits ${fmtHours(Number(hours) || 0)} for ${dayLabel(date)} in its place, in one step. They will see the reason on their own record.`}
          >
            <TextInput value={reason} onChange={setReason} />
          </Field>
        )}
        {errorAt('form') && <InlineStatus tone="error">{errorAt('form')}</InlineStatus>}
      </div>
    </Dialog>
  );
}
